import AlignTopIcon from '@ubnt/icons/AlignTopIcon';
import { DistanceUnitsEnum } from '@ubnt/link-core/enums';
import { useNumberInput } from '@ubnt/link-core-ui/hooks/useNumberInput';
import Input from '@ubnt/ui-components/Input/Input';
import convert from 'convert-units';
import { memo, useCallback, useMemo } from 'react';

import SettingsTile from './SettingsTile';

const MIN_CEILING_HEIGHT = 1;
const MAX_CEILING_HEIGHT = 100;

interface CeilingHeightTileProps {
  ceilingHeight: number;
  unit: ValuesOf<typeof DistanceUnitsEnum>;
  onCeilingHeightChange: (newHeight: number) => void;
}

const CeilingHeightTileImpl = (props: CeilingHeightTileProps) => {
  const { ceilingHeight, unit, onCeilingHeightChange } = props;

  const controlledValue = useMemo(() => {
    if (unit === DistanceUnitsEnum.Metric) {
      return ceilingHeight;
    }
    return convert(ceilingHeight).from('m').to('ft');
  }, [ceilingHeight, unit]);

  const minHeight = useMemo(() => {
    if (unit === DistanceUnitsEnum.Metric) {
      return MIN_CEILING_HEIGHT;
    }
    return convert(MIN_CEILING_HEIGHT).from('m').to('ft');
  }, [unit]);

  const maxHeight = useMemo(() => {
    if (unit === DistanceUnitsEnum.Metric) {
      return MAX_CEILING_HEIGHT;
    }
    return convert(MAX_CEILING_HEIGHT).from('m').to('ft');
  }, [unit]);

  const onChange = useCallback(
    (value: number) => {
      if (unit === DistanceUnitsEnum.Metric) {
        onCeilingHeightChange(value);
      } else {
        onCeilingHeightChange(convert(value).from('ft').to('m'));
      }
    },
    [onCeilingHeightChange, unit]
  );

  const { value, handleChange, handleBlur, increment, decrement } = useNumberInput(
    controlledValue,
    onChange,
    minHeight,
    maxHeight,
    undefined,
    6
  );

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'ArrowUp') {
        increment(event);
      }
      if (event.key === 'ArrowDown') {
        decrement(event);
      }
    },
    [increment, decrement]
  );

  const unitLabel = unit === DistanceUnitsEnum.Metric ? 'm' : 'ft';

  return (
    <SettingsTile icon={AlignTopIcon} title="Ceiling Height">
      <Input
        aria-label="Ceiling Height"
        contentAfter={unitLabel}
        onBlur={handleBlur}
        onChange={handleChange}
        onKeyDown={onKeyDown}
        value={value}
        variant="secondary"
        width={116}
      />
    </SettingsTile>
  );
};

const CeilingHeightTile = memo(CeilingHeightTileImpl);

export default CeilingHeightTile;
